/**
 * /circle-invite/:token — magic-link landing for a family invited into a
 * circle. Sibling of /invite/:token, but instead of joining an existing
 * household the recipient creates their OWN: they set a family name, a
 * display name, and a PIN, and on accept their new household is created
 * and auto-joined to the inviting circle.
 *
 * Authed path: a different account is already signed in — we don't
 * silently switch them. Show a "sign out first" CTA; the token stays
 * valid until they come back.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Preview {
  email: string;
  displayName: string | null;
  circleName: string | null;
  inviterDisplayName: string | null;
  expiresAt: number;
}

export default function CircleInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const auth = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api
      .get<Preview>(`/auth/circle-invite/${encodeURIComponent(token)}`)
      .then((p) => {
        if (!alive) return;
        setPreview(p);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr(
          e instanceof ApiError ? e.message : 'This invitation link is no longer valid.',
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token || !preview) return;
    const fd = new FormData(e.currentTarget);
    const pin = String(fd.get('pin'));
    const confirm = String(fd.get('confirmPin'));
    if (pin !== confirm) {
      setErr("PINs don't match.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await api.post(`/auth/circle-invite/${encodeURIComponent(token)}/accept`, {
        familyName: String(fd.get('familyName')),
        displayName: String(fd.get('displayName')),
        pin,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      // The session cookie is now set. Refresh /me and let the router
      // land us on Today.
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/today', { replace: true });
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : 'Could not accept the invitation. Try again.',
      );
      setSubmitting(false);
    }
  }

  if (auth.state === 'loading') {
    return (
      <div className="min-h-full flex items-center justify-center bg-paper">
        <div className="text-soft text-sm">Loading…</div>
      </div>
    );
  }

  if (auth.state === 'signed-in') {
    return (
      <div className="min-h-full bg-paper flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="font-serif text-2xl text-ink">You're already signed in</h1>
          <p className="text-soft text-sm">
            You're signed in as {auth.me.member.displayName}. Sign out first if you'd like
            to set up a new household from this invitation.
          </p>
          <button
            onClick={() => navigate('/you')}
            className="h-11 px-5 rounded-xl bg-oxblood text-paper font-medium"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  if (err && !preview) {
    return (
      <div className="min-h-full bg-paper flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="font-serif text-2xl text-ink">Invitation unavailable</h1>
          <p className="text-soft text-sm">{err}</p>
          <button
            onClick={() => navigate('/')}
            className="h-11 px-5 rounded-xl bg-oxblood text-paper font-medium"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-full flex items-center justify-center bg-paper">
        <div className="text-soft text-sm">Loading invitation…</div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper flex items-center justify-center p-6">
      <div className="max-w-sm w-full space-y-5">
        <div className="text-center space-y-2">
          <div className="kicker text-sage">Generations invite</div>
          <h1 className="font-serif text-2xl text-ink">
            Join {preview.circleName ?? 'this circle'}
          </h1>
          <p className="text-soft text-sm">
            {preview.inviterDisplayName ?? 'A family'} invited your family to share
            photos, prayers, and chat. Set up your own household to join — your space
            stays private except what you choose to share.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label="Family name"
            name="familyName"
            placeholder="The Smiths"
            required
          />
          <Field
            label="Your name"
            name="displayName"
            placeholder="Grandma"
            defaultValue={preview.displayName ?? ''}
            required
          />
          <PinField label="Pick a PIN (4–8 digits)" name="pin" />
          <PinField label="Confirm PIN" name="confirmPin" />

          <div className="text-xs text-soft text-center">
            Signing up as <span className="text-ink">{preview.email}</span>
          </div>

          {err && <div className="text-oxblood text-sm text-center">{err}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl bg-oxblood text-paper font-medium disabled:opacity-50"
          >
            {submitting ? 'Setting up…' : 'Set up & join'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  ...rest
}: {
  label: string;
  name: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-sm text-soft mb-1">{label}</span>
      <input
        name={name}
        className="w-full h-12 rounded-xl border border-line bg-card px-3 focus:outline-none focus:border-oxblood"
        {...rest}
      />
    </label>
  );
}

function PinField({ label, name }: { label: string; name: string }) {
  return (
    <label className="block">
      <span className="block text-sm text-soft mb-1">{label}</span>
      <input
        name={name}
        type="password"
        inputMode="numeric"
        pattern="\d{4,8}"
        maxLength={8}
        required
        className="w-full h-12 rounded-xl border border-line bg-card px-3 text-xl tracking-[0.3em] font-mono focus:outline-none focus:border-oxblood"
      />
    </label>
  );
}
